import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Button from './ui/Button';
import { parseWeekSeries } from '../utils/timeUtils';
import { previewDraftPublish } from '../services/apiService';
import AcademicYearService from '../services/AcademicYearService';
import ScheduleEntry from '../models/ScheduleEntry';
import { mapScheduleEntryFromApi } from '../mappers/scheduleEntryApiMapper';

const PUBLISH_STRATEGY_REPLACE = 'replace';
const PUBLISH_STRATEGY_ADDITIVE = 'additive';
const REVIEW_DECISION_KEEP_LIVE = 'keep_live';
const REVIEW_DECISION_REPLACE_WITH_DRAFT = 'replace_with_draft';

// ─── Preview data types ───────────────────────────────────────────────────────

interface PreviewStats {
    liveBeforeCount: number;
    liveAfterCount: number;
    draftEntriesCount: number;
}

interface Stats {
    liveBeforeCount: number;
    liveAfterCount: number;
    draftEntriesCount: number;
    // Replace mode
    addedCount: number;
    removedCount: number;
    modifiedCount: number;
    // Additive mode
    newEntriesCount: number;
    reviewMatchesCount: number;
    keptLiveEntriesCount: number;
    draftDuplicatesCount: number;
}

interface ReviewMatch {
    reviewKey: string;
    matchSource: string;
    publishIdentity: string | null;
    defaultDecision: string;
    draftEntry: ScheduleEntry;
    liveEntries: ScheduleEntry[];
}

interface DraftDuplicate {
    reviewKey: string;
    matchSource: string;
    publishIdentity: string | null;
    draftEntries: ScheduleEntry[];
    liveEntries: ScheduleEntry[];
}

interface PreviewData {
    stats?: PreviewStats;
    changes?: {
        added: ScheduleEntry[];
        removed: ScheduleEntry[];
        modified: ScheduleEntry[];
    };
    finalLiveEntries?: ScheduleEntry[];
    newEntries?: ScheduleEntry[];
    reviewMatches?: ReviewMatch[];
    keptLiveEntries?: ScheduleEntry[];
    draftDuplicates?: DraftDuplicate[];
}

/** Convert all raw entry objects inside the server preview response to ScheduleEntry instances. */
function convertPreviewData(raw: any): PreviewData {
    const toEntries = (arr: any[]): ScheduleEntry[] =>
        (arr || []).map((e: any) => new ScheduleEntry(mapScheduleEntryFromApi(e)));

    return {
        stats: raw.stats,
        changes: raw.changes
            ? {
                added: toEntries(raw.changes.added),
                removed: toEntries(raw.changes.removed),
                modified: toEntries(raw.changes.modified),
            }
            : undefined,
        finalLiveEntries: toEntries(raw.finalLiveEntries),
        newEntries: toEntries(raw.newEntries),
        keptLiveEntries: toEntries(raw.keptLiveEntries),
        reviewMatches: (raw.reviewMatches || []).map((m: any) => ({
            reviewKey: m.reviewKey,
            matchSource: m.matchSource,
            publishIdentity: m.publishIdentity ?? null,
            defaultDecision: m.defaultDecision,
            draftEntry: new ScheduleEntry(mapScheduleEntryFromApi(m.draftEntry)),
            liveEntries: toEntries(m.liveEntries),
        })),
        draftDuplicates: (raw.draftDuplicates || []).map((d: any) => ({
            reviewKey: d.reviewKey,
            matchSource: d.matchSource,
            publishIdentity: d.publishIdentity ?? null,
            draftEntries: toEntries(d.draftEntries),
            liveEntries: toEntries(d.liveEntries),
        })),
    };
}

/** Trimmed lowercase for case-insensitive comparison. */
function normStr(s: string | undefined | null): string {
    return (s ?? '').trim().toLowerCase();
}

/**
 * Teachers as a sorted multiset of names (ignores association order / indexes).
 * Falls back to legacy single teacher, then host for events.
 */
function teacherIdentityKey(entry: ScheduleEntry): string {
    const fromMulti = (entry.teachersArray || [])
        .map((t) => normStr((t as { name?: string }).name))
        .filter(Boolean)
        .sort();
    if (fromMulti.length > 0) {
        return fromMulti.join('\u0001');
    }
    const legacy = normStr(entry.teacher);
    if (legacy) return legacy;
    return normStr(entry.host);
}

/** Groups as sorted stable keys (prefer id when present). */
function groupsIdentityKey(entry: ScheduleEntry): string {
    if (!entry.groups?.length) return '';
    return entry.groups
        .map((g) => {
            if (g == null) return '';
            if (typeof g === 'object') {
                const o = g as { id?: number | string; name?: string; group_code?: string };
                if (o.id != null && o.id !== '') return `id:${String(o.id)}`;
                return normStr(o.name || o.group_code || '');
            }
            return normStr(String(g));
        })
        .filter(Boolean)
        .sort()
        .join('\u0001');
}

function normalizedWeekSeries(ws: string | undefined): string {
    return (ws ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Compare scheduling/content fields only — not ids, source, conflicts, etc. */
function entriesEqualOnRelevantFields(a: ScheduleEntry, b: ScheduleEntry): boolean {
    if (normStr(a.displayName) !== normStr(b.displayName)) return false;
    if (normStr(a.type) !== normStr(b.type)) return false;
    if (normStr(a.day) !== normStr(b.day)) return false;
    if (normStr(a.timeStart) !== normStr(b.timeStart)) return false;
    if (normStr(a.timeEnd) !== normStr(b.timeEnd)) return false;
    if (normStr(a.room) !== normStr(b.room)) return false;
    if (normalizedWeekSeries(a.weekSeries) !== normalizedWeekSeries(b.weekSeries)) return false;
    if (teacherIdentityKey(a) !== teacherIdentityKey(b)) return false;
    if (groupsIdentityKey(a) !== groupsIdentityKey(b)) return false;
    if (normStr(a.description) !== normStr(b.description)) return false;
    if (normStr(a.location) !== normStr(b.location)) return false;
    if (normStr(a.notes) !== normStr(b.notes)) return false;
    return true;
}

/** True when the user should see this row: draft differs from at least one live row on relevant fields. */
function reviewMatchHasRelevantDiff(match: ReviewMatch): boolean {
    if (!match.liveEntries?.length) return true;
    const draft = match.draftEntry;
    return match.liveEntries.some((live) => !entriesEqualOnRelevantFields(draft, live));
}

// ─── Small presentational components ─────────────────────────────────────────

interface SummaryCardProps {
    label: string;
    value: number;
    delta?: string;
    tone?: 'gray' | 'green' | 'blue' | 'amber' | 'red';
    onClick?: () => void;
}

const SummaryCard = ({ label, value, delta, tone = 'gray', onClick }: SummaryCardProps) => {
    const toneClasses: Record<string, string> = {
        gray: 'bg-gray-50 text-gray-900',
        green: 'bg-green-50 text-green-900',
        blue: 'bg-blue-50 text-blue-900',
        amber: 'bg-amber-50 text-amber-900',
        red: 'bg-red-50 text-red-900',
    };

    const interactive = Boolean(onClick);
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!onClick) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
        }
    };

    return (
        <div
            className={`rounded-lg p-4 ${toneClasses[tone] ?? toneClasses.gray} ${interactive ? 'cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2' : ''
                }`}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-label={interactive ? `Open ${label} tab` : undefined}
        >
            <div className="text-sm opacity-80 mb-1">{label}</div>
            <div className="text-3xl font-bold">{value}</div>
            {delta !== undefined && <div className="text-xs mt-1">{delta}</div>}
        </div>
    );
};

interface EntryCardProps {
    entry: ScheduleEntry;
    tone?: 'gray' | 'green' | 'red' | 'blue' | 'amber';
    extra?: React.ReactNode;
}

const EntryCard = ({ entry, tone = 'gray', extra = null }: EntryCardProps) => {
    const toneClasses: Record<string, string> = {
        gray: 'border-gray-200 bg-white',
        green: 'border-green-200 bg-green-50',
        red: 'border-red-200 bg-red-50',
        blue: 'border-blue-200 bg-blue-50',
        amber: 'border-amber-200 bg-amber-50',
    };

    const details = `${entry.day || 'N/A'}, ${entry.timeStart || ''}-${entry.timeEnd || ''}, ${entry.room || 'N/A'}`;
    const hasTeacherNames = Boolean(entry.teachersFormatted || entry.teacher);
    const peopleLabel = hasTeacherNames ? 'Teachers' : entry.host ? 'Host' : null;
    const peopleValue = entry.teachersFormatted || entry.teacher || entry.host || '';
    const groupsText = entry.groupsFormatted;

    return (
        <div className={`border rounded-lg p-3 ${toneClasses[tone] ?? toneClasses.gray}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900">{entry.displayName}</div>
                    <div className="text-xs text-gray-600 mt-1">{details}</div>
                    {peopleLabel && peopleValue ? (
                        <div className="text-xs text-gray-600 mt-1">
                            <span className="text-gray-500">{peopleLabel}:</span> {peopleValue}
                        </div>
                    ) : null}
                    {groupsText ? (
                        <div className="text-xs text-gray-600 mt-0.5">
                            <span className="text-gray-500">Groups:</span> {groupsText}
                        </div>
                    ) : null}
                    <div className="text-xs text-gray-500 mt-1">Weeks: {entry.weekSeries || 'N/A'}</div>
                </div>
                {extra && <div className="text-xs text-right text-gray-600 shrink-0">{extra}</div>}
            </div>
        </div>
    );
};

interface DuplicateCardProps {
    duplicate: DraftDuplicate;
}

const DuplicateCard = ({ duplicate }: DuplicateCardProps) => (
    <div className="border border-red-200 bg-red-50 rounded-lg p-3">
        <div className="text-sm font-semibold text-red-900">Duplicate draft match key</div>
        <div className="text-xs text-red-700 mt-1">
            {duplicate.matchSource === 'publish_identity' ? 'Shared identity' : 'Shared fallback key'}:{' '}
            {duplicate.publishIdentity || duplicate.reviewKey}
        </div>
        <div className="mt-3 space-y-2">
            {duplicate.draftEntries.map((entry) => (
                <EntryCard key={`dup-${duplicate.reviewKey}-${entry.id}`} entry={entry} tone="red" />
            ))}
        </div>
    </div>
);

interface ReviewCardProps {
    match: ReviewMatch;
    decision: string;
    onDecisionChange: (reviewKey: string, decision: string) => void;
}

const ReviewCard = ({ match, decision, onDecisionChange }: ReviewCardProps) => (
    <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
            <select
                value={decision}
                onChange={(event) => onDecisionChange(match.reviewKey, event.target.value)}
                className="px-2 py-1 text-sm border border-amber-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
                <option value={REVIEW_DECISION_KEEP_LIVE}>Keep live version</option>
                <option value={REVIEW_DECISION_REPLACE_WITH_DRAFT}>Replace with draft version</option>
            </select>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
                <div className="text-xs font-medium text-gray-600 mb-2">Draft entry</div>
                <EntryCard entry={match.draftEntry} tone="blue" />
            </div>
            <div>
                <div className="text-xs font-medium text-gray-600 mb-2">
                    Existing live entr{match.liveEntries.length === 1 ? 'y' : 'ies'}
                </div>
                <div className="space-y-2">
                    {match.liveEntries.map((entry) => (
                        <EntryCard key={`live-${match.reviewKey}-${entry.id}`} entry={entry} tone="amber" />
                    ))}
                </div>
            </div>
        </div>
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface TimePeriod {
    id: string | number;
    name: string;
    start_week: number;
    end_week: number;
}

interface WeekRange {
    startWeek: number;
    endWeek: number;
}

export interface PublishOptions {
    weekRange: WeekRange | null;
    publishMode: 'replace' | 'additive';
    reviewDecisions: Record<string, string>;
}

interface PublishDraftModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPublish: (options: PublishOptions) => Promise<void> | void;
    currentDraftName: string;
    timePeriods: TimePeriod[];
    scheduledInstances: ScheduleEntry[];
    selectedYearForView: number;
}

const PublishDraftModal = ({
    isOpen,
    onClose,
    onPublish,
    currentDraftName,
    timePeriods,
    scheduledInstances,
    selectedYearForView,
}: PublishDraftModalProps) => {
    const [scopeMode, setScopeMode] = useState<'all' | 'period' | 'custom'>('all');
    const [publishStrategy, setPublishStrategy] = useState(PUBLISH_STRATEGY_REPLACE);
    const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
    const [customStartWeek, setCustomStartWeek] = useState('');
    const [customEndWeek, setCustomEndWeek] = useState('');
    const [isPublishing, setIsPublishing] = useState(false);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState('overview');
    const [previewData, setPreviewData] = useState<PreviewData | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [yearStructure, setYearStructure] = useState<any>(null);
    const [reviewDecisions, setReviewDecisions] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isOpen) {
            setScopeMode('all');
            setPublishStrategy(PUBLISH_STRATEGY_REPLACE);
            setSelectedPeriodId(null);
            setCustomStartWeek('');
            setCustomEndWeek('');
            setWarnings([]);
            setActiveTab('overview');
            setReviewDecisions({});
        }
    }, [isOpen]);

    useEffect(() => {
        setActiveTab('overview');
    }, [publishStrategy]);

    const effectiveWeekRange = useMemo((): WeekRange | null => {
        if (scopeMode === 'all') {
            return null;
        }

        if (scopeMode === 'period' && selectedPeriodId) {
            const period = timePeriods.find((item) => String(item.id) === String(selectedPeriodId));
            if (period && period.start_week && period.end_week) {
                return { startWeek: period.start_week, endWeek: period.end_week };
            }
        }

        if (scopeMode === 'custom') {
            const start = parseInt(customStartWeek, 10);
            const end = parseInt(customEndWeek, 10);
            if (!isNaN(start) && !isNaN(end) && start >= 1 && start <= 53 && end >= 1 && end <= 53) {
                return { startWeek: start, endWeek: end };
            }
        }

        return null;
    }, [scopeMode, selectedPeriodId, customStartWeek, customEndWeek, timePeriods]);

    const isEntryInRange = useCallback((entry: ScheduleEntry, startWeek: number, endWeek: number) => {
        const weekSeries = entry.weekSeries;
        if (!weekSeries) return false;
        try {
            const weeks = parseWeekSeries(weekSeries);
            if (weeks.length === 0) return false;
            const firstWeek = Math.min(...weeks);
            return startWeek <= endWeek
                ? firstWeek >= startWeek && firstWeek <= endWeek
                : firstWeek >= startWeek || firstWeek <= endWeek;
        } catch {
            return false;
        }
    }, []);

    const getEntryAcademicYearStart = useCallback((entry: ScheduleEntry) => {
        const scope = entry.resolvePeriodScope({
            ...(yearStructure || {}),
            periods: timePeriods
        });
        return scope.academicYearStart;
    }, [timePeriods, yearStructure]);

    useEffect(() => {
        let isMounted = true;

        const loadStructure = async () => {
            if (!isOpen) return;
            try {
                const svc = AcademicYearService.getInstance();
                const structure = await svc.getAcademicYearStructure();
                if (isMounted) {
                    setYearStructure(structure);
                }
            } catch (error) {
                console.warn('Failed to load academic year structure', error);
                if (isMounted) setYearStructure(null);
            }
        };

        loadStructure();
        return () => {
            isMounted = false;
        };
    }, [isOpen]);

    useEffect(() => {
        const fetchPreview = async () => {
            if (!isOpen || !currentDraftName || !selectedYearForView) {
                setPreviewData(null);
                return;
            }

            setIsLoadingPreview(true);
            try {
                const rawData = await (previewDraftPublish as any)(currentDraftName, selectedYearForView, {
                    weekRange: effectiveWeekRange,
                    publishMode: publishStrategy,
                });
                setPreviewData(convertPreviewData(rawData));
            } catch (error) {
                console.error('Error fetching preview data:', error);
                setPreviewData(null);
            } finally {
                setIsLoadingPreview(false);
            }
        };

        fetchPreview();
    }, [isOpen, currentDraftName, selectedYearForView, effectiveWeekRange, publishStrategy]);

    useEffect(() => {
        if (!isOpen || publishStrategy !== PUBLISH_STRATEGY_ADDITIVE) {
            setReviewDecisions({});
            return;
        }

        const matches = previewData?.reviewMatches || [];
        setReviewDecisions((previous) => {
            const next: Record<string, string> = {};
            matches.forEach((match) => {
                next[match.reviewKey] =
                    previous[match.reviewKey] || match.defaultDecision || REVIEW_DECISION_REPLACE_WITH_DRAFT;
            });
            return next;
        });
    }, [isOpen, previewData, publishStrategy]);

    useEffect(() => {
        if (!isOpen || publishStrategy !== PUBLISH_STRATEGY_ADDITIVE) {
            return;
        }

        const localYearEntries = (scheduledInstances || []).filter(
            (entry) => entry && getEntryAcademicYearStart(entry) === selectedYearForView
        );
        const summarize = (entries: ScheduleEntry[]) =>
            entries.map((entry) => ({
                id: entry.id,
                name: entry.displayName,
                publishIdentity: entry.publishIdentity,
                client_id: entry.additionalProps?.client_id ?? null,
                entry_code: entry.additionalProps?.entry_code ?? null,
                weekSeries: entry.weekSeries,
            }));

        console.groupCollapsed('[PublishDraftModal:additive] frontend identity debug');
        console.log('draft', currentDraftName);
        console.log('year', selectedYearForView);
        console.log('localDraftEntries', summarize(localYearEntries));
        console.log('preview.newEntries', summarize(previewData?.newEntries || []));
        console.log(
            'preview.reviewMatches',
            (previewData?.reviewMatches || []).map((match) => ({
                reviewKey: match.reviewKey,
                matchSource: match.matchSource,
                draftEntry: summarize([match.draftEntry])[0],
                liveEntries: summarize(match.liveEntries),
            }))
        );
        console.log('preview.keptLiveEntries', summarize(previewData?.keptLiveEntries || []));
        console.log(
            'preview.draftDuplicates',
            (previewData?.draftDuplicates || []).map((duplicate) => ({
                reviewKey: duplicate.reviewKey,
                matchSource: duplicate.matchSource,
                draftEntries: summarize(duplicate.draftEntries),
                liveEntries: summarize(duplicate.liveEntries),
            }))
        );
        console.groupEnd();
    }, [isOpen, publishStrategy, previewData, scheduledInstances, selectedYearForView, currentDraftName, getEntryAcademicYearStart]);

    useEffect(() => {
        if (!effectiveWeekRange || !scheduledInstances.length) {
            setWarnings([]);
            return;
        }

        const newWarnings: string[] = [];
        const { startWeek, endWeek } = effectiveWeekRange;
        const isWeekInRange = (week: number) =>
            startWeek <= endWeek
                ? week >= startWeek && week <= endWeek
                : week >= startWeek || week <= endWeek;

        const yearEntries = scheduledInstances.filter(
            (entry) => entry.isScheduled() && getEntryAcademicYearStart(entry) === selectedYearForView
        );

        yearEntries.forEach((entry) => {
            if (!entry.weekSeries) return;

            try {
                const weeks = parseWeekSeries(entry.weekSeries);
                if (weeks.length === 0) return;

                const firstWeek = Math.min(...weeks);
                const weeksInRange = weeks.filter((week) => isWeekInRange(week));

                if (weeksInRange.length > 0 && !isWeekInRange(firstWeek)) {
                    newWarnings.push(
                        `"${entry.displayName}" starts at week ${firstWeek} (outside range) but has ${weeksInRange.length} week(s) in the selected range`
                    );
                }
            } catch (error) {
                console.error('Error parsing week series for entry:', entry.id, error);
            }
        });

        setWarnings(newWarnings);
    }, [effectiveWeekRange, scheduledInstances, selectedYearForView, getEntryAcademicYearStart]);

    const replacePreview = useMemo(() => {
        const addedCount = previewData?.changes?.added?.length || 0;
        const removedCount = previewData?.changes?.removed?.length || 0;
        const modifiedCount = previewData?.changes?.modified?.length || 0;

        return {
            stats: {
                liveBeforeCount: previewData?.stats?.liveBeforeCount || 0,
                liveAfterCount: previewData?.stats?.liveAfterCount || 0,
                draftEntriesCount: previewData?.stats?.draftEntriesCount || 0,
                addedCount,
                removedCount,
                modifiedCount,
                newEntriesCount: 0,
                reviewMatchesCount: 0,
                keptLiveEntriesCount: 0,
                draftDuplicatesCount: 0,
            } as Stats,
            added: previewData?.changes?.added || [],
            removed: previewData?.changes?.removed || [],
            modified: previewData?.changes?.modified || [],
        };
    }, [previewData]);

    const additivePreview = useMemo(() => {
        const newEntries = previewData?.newEntries || [];
        const reviewMatches = previewData?.reviewMatches || [];
        const reviewMatchesNeedingDiff = reviewMatches.filter(reviewMatchHasRelevantDiff);
        const keptLiveEntries = previewData?.keptLiveEntries || [];
        const draftDuplicates = previewData?.draftDuplicates || [];
        const liveBeforeEntries = [
            ...keptLiveEntries,
            ...reviewMatches.flatMap((match) => match.liveEntries),
        ];
        const liveAfterEntries = [
            ...keptLiveEntries,
            ...reviewMatches.flatMap((match) => {
                const decision =
                    reviewDecisions[match.reviewKey] || match.defaultDecision || REVIEW_DECISION_REPLACE_WITH_DRAFT;
                return decision === REVIEW_DECISION_REPLACE_WITH_DRAFT ? [match.draftEntry] : match.liveEntries;
            }),
            ...newEntries,
        ];

        return {
            newEntries,
            reviewMatches,
            reviewMatchesNeedingDiff,
            keptLiveEntries,
            draftDuplicates,
            liveBeforeEntries,
            liveAfterEntries,
            stats: {
                liveBeforeCount: liveBeforeEntries.length,
                liveAfterCount: liveAfterEntries.length,
                draftEntriesCount: previewData?.stats?.draftEntriesCount || 0,
                newEntriesCount: newEntries.length,
                reviewMatchesCount: reviewMatchesNeedingDiff.length,
                keptLiveEntriesCount: keptLiveEntries.length,
                draftDuplicatesCount: draftDuplicates.length,
                addedCount: 0,
                removedCount: 0,
                modifiedCount: 0,
            } as Stats,
        };
    }, [previewData, reviewDecisions]);

    const isAdditive = publishStrategy === PUBLISH_STRATEGY_ADDITIVE;
    const stats = isAdditive ? additivePreview.stats : replacePreview.stats;

    const weekSequence = useMemo(() => {
        const start = yearStructure?.startWeek;
        const end = yearStructure?.endWeek;
        if (!start || !end) return Array.from({ length: 53 }, (_, index) => index + 1);
        if (start <= end) return Array.from({ length: end - start + 1 }, (_, index) => start + index);
        return [
            ...Array.from({ length: 53 - start + 1 }, (_, index) => start + index),
            ...Array.from({ length: end }, (_, index) => index + 1),
        ];
    }, [yearStructure]);

    const computeWeekCounts = useCallback(
        (entries: ScheduleEntry[]) => {
            const counts = new Map<number, number>();
            const validWeeks = new Set(weekSequence);
            entries.forEach((entry) => {
                if (!entry.weekSeries) return;
                try {
                    parseWeekSeries(entry.weekSeries).forEach((week) => {
                        if (validWeeks.has(week)) {
                            counts.set(week, (counts.get(week) || 0) + 1);
                        }
                    });
                } catch {
                    // Ignore invalid series in preview-only charts.
                }
            });
            return counts;
        },
        [weekSequence]
    );

    const replaceLiveKeptEntries = useMemo(
        () => (previewData?.finalLiveEntries || []).filter((entry) => entry.source === 'existing_live'),
        [previewData]
    );
    const replaceLiveRemovedEntries = useMemo(() => previewData?.changes?.removed || [], [previewData]);
    const replaceDraftEntries = useMemo(
        () => (previewData?.finalLiveEntries || []).filter((entry) => entry.source === 'from_draft'),
        [previewData]
    );
    const replaceLiveBeforeEntries = useMemo(
        () => [...replaceLiveKeptEntries, ...replaceLiveRemovedEntries],
        [replaceLiveKeptEntries, replaceLiveRemovedEntries]
    );
    const replaceLiveAfterEntries = useMemo(() => previewData?.finalLiveEntries || [], [previewData]);

    const chartData = isAdditive
        ? {
            liveBeforeEntries: additivePreview.liveBeforeEntries,
            draftEntries: [
                ...additivePreview.newEntries,
                ...additivePreview.reviewMatches.map((match) => match.draftEntry),
            ],
            liveAfterEntries: additivePreview.liveAfterEntries,
        }
        : {
            liveBeforeEntries: replaceLiveBeforeEntries,
            draftEntries: replaceDraftEntries,
            liveAfterEntries: replaceLiveAfterEntries,
        };

    const liveBeforeCounts = useMemo(
        () => computeWeekCounts(chartData.liveBeforeEntries),
        [chartData.liveBeforeEntries, computeWeekCounts]
    );
    const draftCounts = useMemo(
        () => computeWeekCounts(chartData.draftEntries),
        [chartData.draftEntries, computeWeekCounts]
    );
    const liveAfterCounts = useMemo(
        () => computeWeekCounts(chartData.liveAfterEntries),
        [chartData.liveAfterEntries, computeWeekCounts]
    );

    const unifiedBarMax = useMemo(() => {
        let max = 0;
        weekSequence.forEach((week) => {
            max = Math.max(
                max,
                liveBeforeCounts.get(week) || 0,
                draftCounts.get(week) || 0,
                liveAfterCounts.get(week) || 0
            );
        });
        return Math.max(max, 1);
    }, [weekSequence, liveBeforeCounts, draftCounts, liveAfterCounts]);

    const periodSegments = useMemo(() => {
        if (!yearStructure?.periods || weekSequence.length === 0) {
            return { totalWeeks: weekSequence.length || 1, segments: [] as any[] };
        }

        const indexOfWeek = (week: number) => weekSequence.findIndex((candidate) => candidate === week);
        const segments: any[] = [];

        yearStructure.periods.forEach((period: any, index: number) => {
            const start = period.start_week;
            const end = period.end_week;
            if (start == null || end == null) return;

            if (start <= end) {
                const startIndex = indexOfWeek(start);
                const endIndex = indexOfWeek(end);
                if (startIndex !== -1 && endIndex !== -1) {
                    segments.push({ colorIndex: index, start: startIndex, length: endIndex - startIndex + 1 });
                }
            } else {
                const firstSegmentStart = indexOfWeek(start);
                const secondSegmentEnd = indexOfWeek(end);
                if (firstSegmentStart !== -1) {
                    segments.push({
                        colorIndex: index,
                        start: firstSegmentStart,
                        length: weekSequence.length - firstSegmentStart,
                    });
                }
                if (secondSegmentEnd !== -1) {
                    segments.push({ colorIndex: index, start: 0, length: secondSegmentEnd + 1 });
                }
            }
        });

        return { totalWeeks: weekSequence.length, segments };
    }, [yearStructure, weekSequence]);

    const isWeekInSelectedRange = useCallback(
        (week: number) => {
            if (!effectiveWeekRange) return true;
            const { startWeek, endWeek } = effectiveWeekRange;
            return startWeek <= endWeek
                ? week >= startWeek && week <= endWeek
                : week >= startWeek || week <= endWeek;
        },
        [effectiveWeekRange]
    );

    const MiniScheduleChart = ({
        title,
        counts,
        colorizeRange = false,
    }: {
        title: string;
        counts: Map<number, number>;
        colorizeRange?: boolean;
    }) => {
        const periodColors = ['#E3F2FD', '#E8F5E9', '#FFF3E0', '#F3E5F5', '#E0F2F1', '#FCE4EC'];
        const totalWeeks = weekSequence.length || 1;

        return (
            <div className="w-full">
                <div className="text-sm font-medium text-gray-900 mb-2">{title}</div>
                <div className="relative border rounded p-2 bg-white">
                    <div className="absolute inset-0 pointer-events-none" aria-hidden>
                        {periodSegments.segments.map((segment, index) => {
                            const left = (segment.start / periodSegments.totalWeeks) * 100;
                            const width = (segment.length / periodSegments.totalWeeks) * 100;
                            return (
                                <div
                                    key={`segment-${index}`}
                                    className="absolute top-0 bottom-0"
                                    style={{
                                        left: `${left}%`,
                                        width: `${width}%`,
                                        backgroundColor:
                                            periodColors[segment.colorIndex % periodColors.length],
                                        opacity: 0.35,
                                    }}
                                />
                            );
                        })}
                    </div>
                    <div
                        className="relative grid gap-[2px]"
                        style={{ gridTemplateColumns: `repeat(${totalWeeks}, minmax(0, 1fr))` }}
                    >
                        {weekSequence.map((week) => {
                            const value = counts.get(week) || 0;
                            const rawPct = (value / unifiedBarMax) * 100;
                            const heightPct = value > 0 ? Math.max(rawPct, 20) : 0;
                            const inRange = colorizeRange && isWeekInSelectedRange(week) && value > 0;
                            return (
                                <div
                                    key={`week-${title}-${week}`}
                                    className="flex items-end justify-center min-h-[64px]"
                                >
                                    <div
                                        className={`w-full ${inRange ? 'bg-green-600' : 'bg-blue-600'}`}
                                        style={{ height: `${heightPct}%`, opacity: 0.8 }}
                                        title={`Week ${week}: ${value} entr${value === 1 ? 'y' : 'ies'}`}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div
                        className="mt-1 grid text-[10px] text-gray-500"
                        style={{ gridTemplateColumns: `repeat(${totalWeeks}, minmax(0, 1fr))` }}
                    >
                        {weekSequence.map((week, index) => (
                            <div key={`label-${title}-${week}`} className="text-center">
                                {index % 4 === 0 ? `W${week}` : ''}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const ChartsBlock = () => (
        <div className="space-y-4">
            <MiniScheduleChart title="Live (Before)" counts={liveBeforeCounts} />
            <MiniScheduleChart title="Draft" counts={draftCounts} colorizeRange />
            <MiniScheduleChart title="Live (After)" counts={liveAfterCounts} colorizeRange />
        </div>
    );

    const handleReviewDecisionChange = useCallback((reviewKey: string, decision: string) => {
        setReviewDecisions((previous) => ({
            ...previous,
            [reviewKey]: decision,
        }));
    }, []);

    const handlePublish = async () => {
        setIsPublishing(true);
        try {
            await Promise.resolve(
                onPublish({
                    weekRange: effectiveWeekRange,
                    publishMode: publishStrategy as 'replace' | 'additive',
                    reviewDecisions: isAdditive ? reviewDecisions : {},
                })
            );
            onClose();
        } catch (error) {
            console.error('Error publishing draft:', error);
        } finally {
            setIsPublishing(false);
        }
    };

    const isValidSelection = () => {
        if (scopeMode === 'all') return true;
        if (scopeMode === 'period') return selectedPeriodId !== null;
        if (scopeMode === 'custom') {
            const start = parseInt(customStartWeek, 10);
            const end = parseInt(customEndWeek, 10);
            return !isNaN(start) && !isNaN(end) && start >= 1 && start <= 53 && end >= 1 && end <= 53;
        }
        return false;
    };

    const additiveTabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'new', label: `Will Add (${stats.newEntriesCount})` },
        { id: 'review', label: `Needs Review (${stats.reviewMatchesCount})` },
        { id: 'kept', label: `Kept Live (${stats.keptLiveEntriesCount})` },
    ];
    const replaceTabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'added', label: `Added (${stats.addedCount})` },
        { id: 'removed', label: `Removed (${stats.removedCount})` },
        { id: 'modified', label: `Modified (${stats.modifiedCount})` },
    ];
    const tabs = isAdditive ? additiveTabs : replaceTabs;
    const hasBlockingDuplicates = isAdditive && additivePreview.draftDuplicates.length > 0;
    const publishDisabled = !isValidSelection() || isPublishing || isLoadingPreview || hasBlockingDuplicates;
    const footerMessage = isAdditive
        ? `${stats.newEntriesCount} new, ${stats.reviewMatchesCount} review, ${stats.draftDuplicatesCount} blocking duplicate${stats.draftDuplicatesCount === 1 ? '' : 's'}`
        : `${stats.addedCount + stats.removedCount + stats.modifiedCount} total changes`;

    const renderReplaceList = (entries: ScheduleEntry[], tone: EntryCardProps['tone'], emptyText: string) => (
        <div className="space-y-3">
            {entries.length === 0 ? (
                <div className="text-sm text-gray-500">{emptyText}</div>
            ) : (
                entries.map((entry, index) => (
                    <EntryCard key={`${tone}-${entry.id || index}`} entry={entry} tone={tone} />
                ))
            )}
        </div>
    );

    const renderOverview = () => {
        if (isAdditive) {
            const before = additivePreview.stats.liveBeforeCount;
            const after = additivePreview.stats.liveAfterCount;
            const delta =
                after > before ? `+${after - before}` : after < before ? `${after - before}` : 'no change';

            return (
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Additive Publish Summary</h3>
                    <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                        <SummaryCard label="Live Before" value={before} delta="entries" />
                        <SummaryCard label="Live After" value={after} delta={delta} tone="green" />
                        <SummaryCard
                            label="Will Add"
                            value={stats.newEntriesCount}
                            tone="blue"
                            onClick={() => setActiveTab('new')}
                        />
                        <SummaryCard
                            label="Needs Review"
                            value={stats.reviewMatchesCount}
                            tone="amber"
                            onClick={() => setActiveTab('review')}
                        />
                    </div>
                    <ChartsBlock />
                    {additivePreview.draftDuplicates.length > 0 && (
                        <div className="space-y-3">
                            <div className="p-3 bg-red-50 border border-red-200 rounded">
                                <p className="text-sm text-red-900">
                                    <strong>Publishing blocked:</strong> duplicate draft identities were found in
                                    the selected scope.
                                </p>
                                <p className="text-xs text-red-700 mt-1">
                                    Resolve these duplicates before additive publish can continue.
                                </p>
                            </div>
                            {additivePreview.draftDuplicates.map((duplicate) => (
                                <DuplicateCard key={duplicate.reviewKey} duplicate={duplicate} />
                            ))}
                        </div>
                    )}
                    {effectiveWeekRange && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-sm text-blue-900">
                                <strong>Publishing Range:</strong> Weeks {effectiveWeekRange.startWeek}-
                                {effectiveWeekRange.endWeek}
                            </p>
                            <p className="text-xs text-blue-700 mt-1">
                                Only entries whose first week is in this range are considered for additive
                                publish.
                            </p>
                        </div>
                    )}
                    <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <p className="text-sm text-green-900">
                            <strong>Server Preview:</strong> the additive match list comes from the backend
                            using the same identity logic used during publish.
                        </p>
                    </div>
                </div>
            );
        }

        const delta =
            stats.liveAfterCount > stats.liveBeforeCount
                ? `+${stats.liveAfterCount - stats.liveBeforeCount}`
                : stats.liveAfterCount < stats.liveBeforeCount
                    ? `${stats.liveAfterCount - stats.liveBeforeCount}`
                    : 'no change';

        return (
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Replace Publish Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                    <SummaryCard label="Live Before" value={stats.liveBeforeCount} delta="entries" />
                    <SummaryCard label="Live After" value={stats.liveAfterCount} delta={delta} tone="green" />
                </div>
                <ChartsBlock />
                {effectiveWeekRange && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-sm text-blue-900">
                            <strong>Publishing Range:</strong> Weeks {effectiveWeekRange.startWeek}-
                            {effectiveWeekRange.endWeek}
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                            Only courses starting in this range will be replaced. Other Live entries remain
                            unchanged.
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const renderActiveTab = () => {
        if (isLoadingPreview) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
                        <p className="text-sm text-gray-600">Calculating changes...</p>
                    </div>
                </div>
            );
        }

        if (activeTab === 'overview') {
            return renderOverview();
        }

        if (isAdditive) {
            if (activeTab === 'new') {
                return renderReplaceList(additivePreview.newEntries, 'green', 'No new entries will be added.');
            }
            if (activeTab === 'review') {
                return (
                    <div className="space-y-4">
                        {additivePreview.reviewMatchesNeedingDiff.length === 0 ? (
                            <div className="text-sm text-gray-500">
                                No scheduling differences to review — draft and live match on compared fields.
                            </div>
                        ) : (
                            additivePreview.reviewMatchesNeedingDiff.map((match) => (
                                <ReviewCard
                                    key={match.reviewKey}
                                    match={match}
                                    decision={
                                        reviewDecisions[match.reviewKey] ||
                                        match.defaultDecision ||
                                        REVIEW_DECISION_REPLACE_WITH_DRAFT
                                    }
                                    onDecisionChange={handleReviewDecisionChange}
                                />
                            ))
                        )}
                    </div>
                );
            }
            if (activeTab === 'kept') {
                return renderReplaceList(
                    additivePreview.keptLiveEntries,
                    'gray',
                    'No existing live entries remain untouched in this scope.'
                );
            }
        } else {
            if (activeTab === 'added') {
                return renderReplaceList(replacePreview.added, 'green', 'No entries will be added.');
            }
            if (activeTab === 'removed') {
                return renderReplaceList(
                    replacePreview.removed,
                    'red',
                    'No live entries will be removed.'
                );
            }
            if (activeTab === 'modified') {
                return renderReplaceList(
                    replacePreview.modified,
                    'amber',
                    'No in-place modifications are tracked here. Replace publish is modeled as remove + add.'
                );
            }
        }

        return null;
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-[95vh] flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Publish Draft to Live</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Draft: <span className="font-medium">{currentDraftName}</span> • Year:{' '}
                                {selectedYearForView}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                            title="Close"
                            aria-label="Close"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="flex-1 flex min-h-0">
                    <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
                        <div className="px-6 py-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Publishing Strategy
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            name="publishStrategy"
                                            value={PUBLISH_STRATEGY_REPLACE}
                                            checked={publishStrategy === PUBLISH_STRATEGY_REPLACE}
                                            onChange={() => setPublishStrategy(PUBLISH_STRATEGY_REPLACE)}
                                            className="mr-2 mt-0.5"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">
                                                Replace in scope
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Current behavior: remove live entries in scope, then publish
                                                draft entries.
                                            </div>
                                        </div>
                                    </label>
                                    <label className="flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            name="publishStrategy"
                                            value={PUBLISH_STRATEGY_ADDITIVE}
                                            checked={publishStrategy === PUBLISH_STRATEGY_ADDITIVE}
                                            onChange={() => setPublishStrategy(PUBLISH_STRATEGY_ADDITIVE)}
                                            className="mr-2 mt-0.5"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">
                                                Additive merge
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Keep unmatched live entries, add only new draft entries, and
                                                review identity matches one by one.
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Publishing Scope
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            name="scopeMode"
                                            value="all"
                                            checked={scopeMode === 'all'}
                                            onChange={() => setScopeMode('all')}
                                            className="mr-2 mt-0.5"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">
                                                Entire Draft
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                All entries for this year
                                            </div>
                                        </div>
                                    </label>
                                    <label className="flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            name="scopeMode"
                                            value="period"
                                            checked={scopeMode === 'period'}
                                            onChange={() => setScopeMode('period')}
                                            className="mr-2 mt-0.5"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">
                                                Specific Period
                                            </div>
                                            <div className="text-xs text-gray-500 mb-2">
                                                Entries whose first week falls in the selected period
                                            </div>
                                            {scopeMode === 'period' && (
                                                <select
                                                    value={selectedPeriodId || ''}
                                                    onChange={(event) =>
                                                        setSelectedPeriodId(event.target.value)
                                                    }
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    <option value="">Select period...</option>
                                                    {timePeriods.map((period) => (
                                                        <option key={period.id} value={period.id}>
                                                            {period.name} (W{period.start_week}-
                                                            {period.end_week})
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    </label>
                                    <label className="flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            name="scopeMode"
                                            value="custom"
                                            checked={scopeMode === 'custom'}
                                            onChange={() => setScopeMode('custom')}
                                            className="mr-2 mt-0.5"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">
                                                Custom Week Range
                                            </div>
                                            <div className="text-xs text-gray-500 mb-2">
                                                Enter week numbers (1-53)
                                            </div>
                                            {scopeMode === 'custom' && (
                                                <div
                                                    className="flex items-center gap-2"
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="53"
                                                        value={customStartWeek}
                                                        onChange={(event) =>
                                                            setCustomStartWeek(event.target.value)
                                                        }
                                                        placeholder="Start"
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                    <span className="text-gray-500 text-xs">to</span>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="53"
                                                        value={customEndWeek}
                                                        onChange={(event) =>
                                                            setCustomEndWeek(event.target.value)
                                                        }
                                                        placeholder="End"
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {warnings.length > 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                                    <div className="flex items-start">
                                        <svg
                                            className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <div className="flex-1">
                                            <h4 className="text-xs font-medium text-yellow-800 mb-1">
                                                {warnings.length} Warning{warnings.length > 1 ? 's' : ''}
                                            </h4>
                                            <ul className="text-xs text-yellow-700 space-y-0.5">
                                                {warnings.slice(0, 3).map((warning, index) => (
                                                    <li key={index} className="flex items-start">
                                                        <span className="mr-1">•</span>
                                                        <span className="line-clamp-2">{warning}</span>
                                                    </li>
                                                ))}
                                                {warnings.length > 3 && (
                                                    <li className="text-yellow-600 italic text-xs">
                                                        +{warnings.length - 3} more...
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                <h4 className="text-xs font-medium text-blue-900 mb-1.5">
                                    Publishing Process
                                </h4>
                                <ul className="text-xs text-blue-800 space-y-0.5">
                                    <li>• Draft saved automatically</li>
                                    <li>• Live backup created</li>
                                    <li>
                                        •{' '}
                                        {effectiveWeekRange
                                            ? `Publishes weeks ${effectiveWeekRange.startWeek}-${effectiveWeekRange.endWeek}`
                                            : 'Publishes all entries'}
                                    </li>
                                    <li>
                                        •{' '}
                                        {isAdditive
                                            ? 'Review identity matches before publishing'
                                            : 'Replace live entries inside the selected scope'}
                                    </li>
                                    <li>• Stay in draft mode after</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="w-2/3 flex flex-col">
                        <div className="flex border-b border-gray-200 flex-shrink-0">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === tab.id
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">{renderActiveTab()}</div>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
                    <div className="text-sm text-gray-600">{footerMessage}</div>
                    <div className="flex gap-3">
                        <Button
                            onClick={onClose}
                            variant="secondary"
                            disabled={isPublishing}
                            title="Cancel"
                            aria-label="Cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handlePublish}
                            variant="primary"
                            disabled={publishDisabled}
                            className="bg-green-600 hover:bg-green-700"
                            title={
                                isPublishing
                                    ? 'Publishing...'
                                    : isLoadingPreview
                                        ? 'Calculating changes...'
                                        : 'Publish to Live'
                            }
                            aria-label={
                                isPublishing
                                    ? 'Publishing'
                                    : isLoadingPreview
                                        ? 'Calculating changes'
                                        : 'Publish to Live'
                            }
                        >
                            {isPublishing
                                ? 'Publishing...'
                                : isLoadingPreview
                                    ? 'Calculating...'
                                    : 'Publish to Live'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublishDraftModal;
